import React, { useState, useMemo, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Calendar, Clock, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ConfirmDialog, useConfirmDialog } from './ui/confirm-dialog';

/**
 * Dialog for cancelling recurring meetings with options to:
 * - Cancel only this occurrence
 * - Cancel all occurrences (entire series)
 */
const CancelRecurringMeetingDialog = ({
    open,
    onClose,
    meeting,
    onConfirm,
    isLoading = false,
}) => {
    const [cancelMode, setCancelMode] = useState('this');
    const { isOpen, showConfirm, handleConfirm: handleConfirmAction, handleCancel } = useConfirmDialog();

    // Check if the current occurrence is already cancelled or completed
    const isTodayOccurrenceDisabled = useMemo(() => {
        if (!meeting) return false;
        const cancelledDates = meeting.cancelled_occurrences || [];
        const scheduledDateStr = meeting.scheduled_date; // Already in ISO format (YYYY-MM-DD)
        const isAlreadyCancelled = cancelledDates.includes(scheduledDateStr);
        const isCompleted = meeting.state === 'completed' || meeting.status === 'completed';
        const isOngoing = meeting.state === 'ongoing' || meeting.status === 'ongoing';
        const isPending = meeting.state === 'pending' || meeting.status === 'pending';
        return isAlreadyCancelled || isCompleted || isOngoing || isPending;
    }, [meeting]);

    // Check if there are any future instances to cancel
    const hasFutureCancellableInstances = useMemo(() => {
        if (!meeting) return true; // Default to true if unknown
        // Backend provides this field - if false, all future instances are already cancelled
        return meeting.has_future_cancellable_instances !== false;
    }, [meeting]);

    const disableReason = useMemo(() => {
        if (!meeting) return '';
        const cancelledDates = meeting.cancelled_occurrences || [];
        const isAlreadyCancelled = cancelledDates.includes(meeting.scheduled_date);
        if (isAlreadyCancelled) return 'cancelled';
        if (meeting.state === 'completed' || meeting.status === 'completed') return 'completed';
        if (meeting.state === 'ongoing' || meeting.status === 'ongoing') return 'ongoing';
        if (meeting.state === 'pending' || meeting.status === 'pending') return 'pending';
        return '';
    }, [meeting]);

    // Update default selection when dialog opens or meeting changes
    useEffect(() => {
        if (open && meeting) {
            const cancelledDates = meeting.cancelled_occurrences || [];
            const isAlreadyCancelled = cancelledDates.includes(meeting.scheduled_date);
            const isCompleted = meeting.state === 'completed' || meeting.status === 'completed';
            const isOngoing = meeting.state === 'ongoing' || meeting.status === 'ongoing';
            const isPending = meeting.state === 'pending' || meeting.status === 'pending';

            // If already cancelled, completed, ongoing, or pending, default to 'from_tomorrow'
            setCancelMode(isAlreadyCancelled || isCompleted || isOngoing || isPending ? 'from_tomorrow' : 'this');
        }
    }, [open, meeting]);

    if (!meeting) return null;

    const handleConfirm = () => {
        // Determine cancel_all based on mode
        const isAll = (cancelMode === 'from_today' || cancelMode === 'from_tomorrow');
        const dateStr = meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMMM d, yyyy') : 'this occurrence';

        showConfirm(() => {
            onConfirm(isAll, meeting.scheduled_date, cancelMode);  // Pass cancelMode as 3rd parameter
        });
    };

    const handleClose = () => {
        setCancelMode('this'); // Reset to default
        onClose();
    };

    const formatTime = (time) => {
        if (!time) return 'N/A';
        const [hours, minutes] = time.split(':');
        const hour = parseInt(hours);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        return `${displayHour}:${minutes} ${ampm}`;
    };

    const getRecurrenceLabel = () => {
        if (meeting.recurrence_label) return meeting.recurrence_label;
        switch (meeting.recurrence) {
            case 'daily': return 'Daily';
            case 'weekly': return 'Weekly';
            case 'monthly': return 'Monthly';
            case 'yearly': return 'Yearly';
            case 'custom': return 'Custom';
            default: return meeting.recurrence;
        }
    };

    return (
        <>
            <Dialog open={open} onOpenChange={handleClose}>
                <DialogContent className="sm:max-w-[500px] w-[95vw] ">
                    <DialogHeader className='fixed top-0 left-0 right-0 bg-white   p-6 border-b z-10'>
                        <DialogTitle className="flex  items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Cancel Recurring Meeting
                        </DialogTitle>
                        <DialogDescription>
                            This is a recurring meeting. How would you like to cancel it?
                        </DialogDescription>
                    </DialogHeader>
<div className='h-[440px] rounded-lg overflow-y-auto mt-10 flex flex-col gap-3'>

                    {/* Meeting Details Card */}
                    <div className="rounded-lg flex items-center justify-between flex-wrap mt-14 sm:mt-10 border bg-gray-50 p-4 ">
                        <h3 className="font-semibold text-gray-900">{meeting.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="h-4 w-4" />
                            <span>{getRecurrenceLabel()}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Clock className="h-4 w-4" />
                            <span>
                                {formatTime(meeting.start_time)} - {formatTime(meeting.end_time)}
                            </span>
                        </div>
                        {meeting.participant_count > 0 && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Users className="h-4 w-4" />
                                <span>{meeting.participant_count} Participants</span>
                            </div>
                        )}
                    </div>

                    {/* Cancel Mode Selection */}
                    <RadioGroup
                        value={cancelMode}
                        onValueChange={setCancelMode}
                        className="space-y-4  mb-20 sm:mb-10"
                    >
                        {/* Option 1: Cancel This Occurrence Only */}
                        <div
                            className={`flex items-start space-x-3 rounded-lg border p-4 transition-colors ${isTodayOccurrenceDisabled
                                ? 'bg-gray-50 border-gray-200 opacity-75 cursor-not-allowed'
                                : 'hover:bg-gray-50 cursor-pointer'
                                }`}
                            onClick={() => !isTodayOccurrenceDisabled && setCancelMode('this')}
                        >
                            <RadioGroupItem
                                value="this"
                                id="cancel-this"
                                className="flex items-center  justify-center mt-1 h-5 w-5"
                                disabled={isTodayOccurrenceDisabled}
                            />
                            <div className="flex-1 rounded-lg">
                                <Label
                                    htmlFor="cancel-this"
                                    className={`font-medium ${isTodayOccurrenceDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                >
                                    {disableReason === 'cancelled' ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-green-600" />
                                            This occurrence is already cancelled
                                        </span>
                                    ) : disableReason === 'completed' ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-blue-600" />
                                            This occurrence is already completed
                                        </span>
                                    ) : disableReason === 'ongoing' ? (
                                        <span className="flex items-center gap-2">
                                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                                            This occurrence is currently ongoing
                                        </span>
                                    ) : disableReason === 'pending' ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-gray-600" />
                                            This occurrence has ended (pending MOM)
                                        </span>
                                    ) : (
                                        'Cancel this meeting only'
                                    )}
                                </Label>
                                <p className="text-sm text-gray-500 mt-1">
                                    {disableReason === 'cancelled' ? (
                                        `${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'EEEE, MMMM d, yyyy') : 'This date'} has already been cancelled.`
                                    ) : disableReason === 'completed' ? (
                                        'This occurrence has already taken place and cannot be cancelled.'
                                    ) : disableReason === 'ongoing' ? (
                                        'This occurrence is already in progress and cannot be cancelled separately.'
                                    ) : disableReason === 'pending' ? (
                                        'This occurrence has ended and is awaiting Minutes of Meeting. Only future occurrences can be cancelled.'
                                    ) : (
                                        `Only ${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'EEEE, MMMM d, yyyy') : 'this date'} will be cancelled. Future occurrences will continue as scheduled.`
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Option 2: Cancel From Today Onwards */}
                        <div
                            className={`flex items-start space-x-3 rounded-lg border p-4 transition-colors ${(isTodayOccurrenceDisabled || !hasFutureCancellableInstances)
                                ? 'bg-gray-50 border-gray-200 opacity-75 cursor-not-allowed'
                                : 'hover:bg-gray-50 cursor-pointer'
                                }`}
                            onClick={() => !(isTodayOccurrenceDisabled || !hasFutureCancellableInstances) && setCancelMode('from_today')}
                        >
                            <RadioGroupItem
                                value="from_today"
                                id="cancel-from-today"
                                className="flex items-center justify-center mt-1 h-5 w-5"
                                disabled={isTodayOccurrenceDisabled || !hasFutureCancellableInstances}
                            />
                            <div className="flex-1">
                                <Label
                                    htmlFor="cancel-from-today"
                                    className={`font-medium ${(isTodayOccurrenceDisabled || !hasFutureCancellableInstances) ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
                                >
                                    {!hasFutureCancellableInstances ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-gray-400" />
                                            No future occurrences to cancel
                                        </span>
                                    ) : isTodayOccurrenceDisabled ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-gray-400" />
                                            Cancel from today onwards (not available)
                                        </span>
                                    ) : (
                                        'Cancel from today onwards'
                                    )}
                                </Label>
                                <p className="text-sm text-gray-500 mt-1">
                                    {!hasFutureCancellableInstances ? (
                                        'All future occurrences have already been cancelled.'
                                    ) : isTodayOccurrenceDisabled ? (
                                        'This occurrence has already started or ended. Use "Cancel from tomorrow onwards" instead.'
                                    ) : (
                                        `${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMMM d') : 'Today'} and all future occurrences will be cancelled. Past meetings with action points are preserved.`
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Option 3: Cancel From Tomorrow Onwards */}
                        <div
                            className={`flex items-start space-x-3 rounded-lg border p-4 transition-colors ${!hasFutureCancellableInstances
                                ? 'bg-gray-50 border-gray-200 opacity-75 cursor-not-allowed'
                                : 'hover:bg-gray-50 cursor-pointer'
                                }`}
                            onClick={() => hasFutureCancellableInstances && setCancelMode('from_tomorrow')}
                        >
                            <RadioGroupItem 
                                value="from_tomorrow" 
                                id="cancel-from-tomorrow" 
                                className="flex items-center justify-center mt-1 h-5 w-5"
                                disabled={!hasFutureCancellableInstances}
                            />
                            <div className="flex-1">
                                <Label 
                                    htmlFor="cancel-from-tomorrow" 
                                    className={`font-medium ${!hasFutureCancellableInstances ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer'}`}
                                >
                                    {!hasFutureCancellableInstances ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="h-4 w-4 text-gray-400" />
                                            No future occurrences to cancel
                                        </span>
                                    ) : (
                                        'Cancel from tomorrow onwards'
                                    )}
                                </Label>
                                <p className="text-sm text-gray-500 mt-1">
                                    {!hasFutureCancellableInstances ? (
                                        'All future occurrences have already been cancelled.'
                                    ) : isTodayOccurrenceDisabled ? (
                                        `All future occurrences will be cancelled. This occurrence and past meetings are preserved.`
                                    ) : (
                                        `Today's meeting (${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMMM d') : 'today'}) will continue as planned. All future occurrences will be cancelled.`
                                    )}
                                </p>
                            </div>
                        </div>
                    </RadioGroup>
</div>

                    <DialogFooter className="fixed bottom-0 left-0 p-2  right-0 bg-white border-t gap-2 sm:gap-0">
                        <Button
                            variant="outline"
                            onClick={handleClose}
                            disabled={isLoading}
                        >
                            Keep Meeting
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirm}
                            disabled={isLoading || (cancelMode === 'this' && isTodayOccurrenceDisabled) || (cancelMode === 'from_today' && (isTodayOccurrenceDisabled || !hasFutureCancellableInstances)) || (cancelMode === 'from_tomorrow' && !hasFutureCancellableInstances)}
                        >
                            {isLoading ? 'Cancelling...' : 'Cancel Meeting'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={isOpen}
                onOpenChange={handleCancel}
                onConfirm={handleConfirmAction}
                title={
                    cancelMode === 'this'
                        ? 'Cancel This Meeting?'
                        : cancelMode === 'from_today'
                            ? 'Cancel From Today Onwards?'
                            : 'Cancel From Tomorrow Onwards?'
                }
                description={
                    cancelMode === 'this'
                        ? `This will cancel only the meeting on ${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMMM d, yyyy') : 'this date'}.`
                        : cancelMode === 'from_today'
                            ? `This will cancel ${meeting.scheduled_date ? format(new Date(meeting.scheduled_date), 'MMMM d') : 'today'} and all future occurrences. This action cannot be undone.`
                            : 'This will cancel all future occurrences starting from tomorrow. This action cannot be undone.'
                }
                confirmText="Yes, Cancel"
                cancelText="No, Keep"
                variant="destructive"
                icon="danger"
            />
        </>
    );
};

export default CancelRecurringMeetingDialog;
